import type { MessageKey } from "../../shared/i18n/messages.de";
import type { IconName } from "../../shared/ui/Icon";
import type { PlatformAccess } from "./platformAccess";

/** Paths of the operator area (F-69); further pages of the area (F-70, F-79) add theirs here. */
export const platformPaths = {
  root: "/platform",
  tenants: "/platform/tenants",
  onboarding: "/platform/onboarding",
  signups: "/platform/signups",
  billing: "/platform/billing",
  legalDocuments: "/platform/legal-documents",
  subprocessors: "/platform/subprocessors",
  securityIncidents: "/platform/security-incidents",
  contractEnd: "/platform/contract-end",
  databases: "/platform/databases",
  auditLog: "/platform/audit-log",
  plans: "/platform/plans",
  systemStatus: "/platform/system-status",
  staffAccounts: "/platform/staff-accounts",
  account: "/platform/account",
} as const;

/**
 * Returns the path of a page of the operator area with a tenant selected (`?tenant=`), for the pages
 * billing, end of the contract and databases (F-70) and the platform audit (F-79).
 *
 * @param path - The page.
 * @param tenantId - The tenant.
 * @returns The path with the tenant.
 */
export function platformTenantSelectionPath(path: string, tenantId: string): string {
  const query = new URLSearchParams({ tenant: tenantId });
  const selected = `${path}?${query.toString()}`;
  return selected;
}

/**
 * Returns the path of a tenant's detail page.
 *
 * @param tenantId - The tenant.
 * @returns The path.
 */
export function platformTenantPath(tenantId: string): string {
  const path = `${platformPaths.tenants}/${tenantId}`;
  return path;
}

/**
 * Returns the path of the state of an onboarding.
 *
 * @param tenantId - The tenant of the onboarding.
 * @returns The path.
 */
export function platformOnboardingPath(tenantId: string): string {
  const path = `/platform/onboardings/${tenantId}`;
  return path;
}

/**
 * Returns whether a path belongs to the operator area.
 *
 * @param pathname - The path.
 * @returns `true` for `/platform` and every path below it.
 */
export function isPlatformPath(pathname: string): boolean {
  const inArea = pathname === platformPaths.root || pathname.startsWith(`${platformPaths.root}/`);
  return inArea;
}

/** An entry of the navigation of the operator area. */
export interface PlatformNavigationItem {
  /** Route path. */
  readonly path: string;
  /** Label message key. */
  readonly labelKey: MessageKey;
  /** Icon shown next to the label. */
  readonly icon: IconName;
  /**
   * The access the entry needs: `read` for operator support and administrators, `manage` for
   * administrators only (pages that only change something).
   */
  readonly requires: Exclude<PlatformAccess, "none">;
}

/**
 * Entries of the operator area in display order. A further page adds one entry here and one route
 * in `platformRoutes.tsx`; the shell, the start page and the guard stay as they are.
 */
export const platformNavigationItems: readonly PlatformNavigationItem[] = [
  {
    path: platformPaths.root,
    labelKey: "platform.nav.overview",
    icon: "balances",
    requires: "read",
  },
  {
    path: platformPaths.tenants,
    labelKey: "platform.nav.tenants",
    icon: "tenant",
    requires: "read",
  },
  {
    path: platformPaths.onboarding,
    labelKey: "platform.nav.onboarding",
    icon: "setup",
    requires: "manage",
  },
  {
    path: platformPaths.signups,
    labelKey: "platform.nav.signups",
    icon: "times",
    requires: "read",
  },
  {
    path: platformPaths.billing,
    labelKey: "platform.nav.billing",
    icon: "billing",
    requires: "read",
  },
  {
    path: platformPaths.legalDocuments,
    labelKey: "platform.nav.legalDocuments",
    icon: "corrections",
    requires: "read",
  },
  {
    path: platformPaths.subprocessors,
    labelKey: "platform.nav.subprocessors",
    icon: "team",
    requires: "read",
  },
  {
    path: platformPaths.securityIncidents,
    labelKey: "platform.nav.securityIncidents",
    icon: "violations",
    requires: "manage",
  },
  {
    path: platformPaths.contractEnd,
    labelKey: "platform.nav.contractEnd",
    icon: "privacy",
    requires: "read",
  },
  {
    path: platformPaths.databases,
    labelKey: "platform.nav.databases",
    icon: "admin",
    requires: "manage",
  },
  {
    path: platformPaths.auditLog,
    labelKey: "platform.nav.auditLog",
    icon: "corrections",
    requires: "read",
  },
  {
    path: platformPaths.plans,
    labelKey: "platform.nav.plans",
    icon: "projects",
    requires: "read",
  },
  {
    path: platformPaths.systemStatus,
    labelKey: "platform.nav.systemStatus",
    icon: "warning",
    requires: "read",
  },
  {
    path: platformPaths.staffAccounts,
    labelKey: "platform.nav.staffAccounts",
    icon: "team",
    requires: "read",
  },
];

/**
 * Number of entries the bottom bar of phones shows next to the account; the other entries are
 * reached from the overview there (F-70), as the bar has room for five entries only.
 */
export const platformBarSize = 4;

/**
 * Filters the entries by the access of the person.
 *
 * @param access - The access.
 * @returns The visible entries in display order.
 */
export function visiblePlatformNavigation(
  access: PlatformAccess,
): readonly PlatformNavigationItem[] {
  const visible = platformNavigationItems.filter(
    (item) => access === "manage" || (access === "read" && item.requires === "read"),
  );

  return visible;
}
